import {
  AutoCompleteDomain,
  AutoCompleteSearchRequest,
  AutoCompleteSearchResult,
  AutoCompleteSelectedValue,
  MetaData,
  OptionItem,
} from "../../models/control";
import { customerAutoCompleteProvider } from "./customer-provider";
import { materialAutoCompleteProvider } from "./material-provider";
import { orderAutoCompleteProvider } from "./order-provider";
import { paperAutoCompleteProvider } from "./paper-provider";
import { productAutoCompleteProvider } from "./product-provider";
import { workOrderAutoCompleteProvider } from "./work-order-provider";

export type AutoCompleteProviderContext = {
  customerId?: string | number | null;
};

export type AutoCompleteProvider = {
  searchOptionItemsAsync: (
    keyword: string,
    context?: AutoCompleteProviderContext,
  ) => Promise<OptionItem[]>;
  searchOptionItemsPagedAsync: (
    keyword: string,
    request: AutoCompleteSearchRequest,
    context?: AutoCompleteProviderContext,
  ) => Promise<AutoCompleteSearchResult>;
  resolveSelectedOptionAsync?: (
    value: AutoCompleteSelectedValue,
    context?: AutoCompleteProviderContext,
  ) => Promise<OptionItem | null>;
};

export type AutoCompleteProviderContextResolver =
  () => AutoCompleteProviderContext;

const PROVIDER_BY_DOMAIN: Record<AutoCompleteDomain, AutoCompleteProvider> = {
  customer: customerAutoCompleteProvider,
  order: orderAutoCompleteProvider,
  workOrder: workOrderAutoCompleteProvider,
  product: productAutoCompleteProvider,
  material: materialAutoCompleteProvider,
  paper: paperAutoCompleteProvider,
};

export const getAutoCompleteProvider = (
  domain?: AutoCompleteDomain | null,
): AutoCompleteProvider | undefined => {
  if (!domain) return undefined;
  return PROVIDER_BY_DOMAIN[domain];
};

export const createAutoCompleteMetaDataByProvider = (
  provider: AutoCompleteProvider,
  getContext?: AutoCompleteProviderContextResolver,
): Pick<
  MetaData,
  | "searchOptionItemsAsync"
  | "searchOptionItemsPagedAsync"
  | "resolveSelectedOptionAsync"
> => {
  return {
    searchOptionItemsAsync: (keyword: string) =>
      provider.searchOptionItemsAsync(keyword, getContext?.()),
    searchOptionItemsPagedAsync: (
      keyword: string,
      request: AutoCompleteSearchRequest,
    ) => provider.searchOptionItemsPagedAsync(keyword, request, getContext?.()),
    resolveSelectedOptionAsync: (value: AutoCompleteSelectedValue) =>
      provider.resolveSelectedOptionAsync?.(value, getContext?.()) ||
      Promise.resolve(null),
  };
};

export const createAutoCompleteMetaDataByDomain = (
  domain: AutoCompleteDomain,
  getContext?: AutoCompleteProviderContextResolver,
) => {
  const provider = getAutoCompleteProvider(domain);
  if (!provider) {
    return undefined;
  }

  return createAutoCompleteMetaDataByProvider(provider, getContext);
};
